import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class GoogleMapsLoaderService {
  private isLoaded = false;
  private isLoading = false;
  private loadPromise: Promise<void> | null = null;
  private readonly LOAD_TIMEOUT = 10000;
  private readonly CHECK_INTERVAL = 100;

  loadGoogleMaps(): Promise<void> {
    if (this.isLoaded) {
      return Promise.resolve();
    }

    if (this.isLoading && this.loadPromise) {
      return this.loadPromise;
    }

    this.isLoading = true;
    this.loadPromise = this.loadGoogleMapsScript();
    return this.loadPromise;
  }

  private loadGoogleMapsScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isGoogleMapsFullyLoaded()) {
        this.isLoaded = true;
        this.isLoading = false;
        resolve();
        return;
      }

      const apiKey = environment.googleMapsApiKey;
      if (!apiKey) {
        this.isLoading = false;
        reject(new Error('Google Maps API key not found in environment'));
        return;
      }

      const script = this.createScript(apiKey);
      script.onload = () => this.waitForInitialization(resolve, reject);
      script.onerror = () => {
        this.isLoading = false;
        reject(new Error('Failed to load Google Maps API script'));
      };

      document.head.appendChild(script);
    });
  }

  private createScript(apiKey: string): HTMLScriptElement {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,marker&loading=async`;
    return script;
  }

  private waitForInitialization(
    resolve: () => void,
    reject: (error: Error) => void
  ): void {
    let attempts = 0;
    const maxAttempts = this.LOAD_TIMEOUT / this.CHECK_INTERVAL;

    const interval = setInterval(() => {
      attempts++;

      if (this.isGoogleMapsFullyLoaded()) {
        clearInterval(interval);
        this.isLoaded = true;
        this.isLoading = false;
        resolve();
        return;
      }

      if (attempts >= maxAttempts) {
        clearInterval(interval);
        this.isLoading = false;
        reject(new Error('Google Maps API initialization timeout'));
      }
    }, this.CHECK_INTERVAL);
  }

  private isGoogleMapsFullyLoaded(): boolean {
    return !!(
      typeof google !== 'undefined' &&
      google.maps &&
      google.maps.Map &&
      google.maps.Geocoder &&
      google.maps.Marker
    );
  }

  isAdvancedMarkerAvailable(): boolean {
    return !!(this.isLoaded && google.maps?.marker?.AdvancedMarkerElement);
  }

  isGoogleMapsLoaded(): boolean {
    return this.isLoaded;
  }

  isLoadingInProgress(): boolean {
    return this.isLoading;
  }
}
